import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Pencil, Trash2, Loader2, Mail, Phone, Briefcase, StickyNote } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientContacts = ({ clientId, token }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create/Edit modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        email: '',
        phone: '',
        notes: ''
    });
    const [saving, setSaving] = useState(false);

    // Delete confirmation state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (clientId) {
            fetchContacts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    async function fetchContacts() {
        try {
            const res = await axios.get(`${API}/clients/${clientId}/contacts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setContacts(res.data);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            toast.error('Error al cargar contactos');
        } finally {
            setLoading(false);
        }
    }

    const handleOpenCreateModal = () => {
        setEditingContact(null);
        setFormData({
            name: '',
            role: '',
            email: '',
            phone: '',
            notes: ''
        });
        setModalOpen(true);
    };

    const handleOpenEditModal = (contact) => {
        setEditingContact(contact);
        setFormData({
            name: contact.name || '',
            role: contact.role || '',
            email: contact.email || '',
            phone: contact.phone || '',
            notes: contact.notes || ''
        });
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingContact(null);
        setFormData({
            name: '',
            role: '',
            email: '',
            phone: '',
            notes: ''
        });
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('El nombre es requerido');
            return;
        }

        setSaving(true);
        try {
            if (editingContact) {
                // Update
                await axios.put(`${API}/contacts/${editingContact.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Contacto actualizado');
            } else {
                // Create
                await axios.post(`${API}/clients/${clientId}/contacts`, {
                    client_id: clientId,
                    ...formData
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Contacto creado');
            }

            handleCloseModal();
            await fetchContacts();
        } catch (error) {
            console.error('Error saving contact:', error);
            toast.error(editingContact ? 'Error al actualizar contacto' : 'Error al crear contacto');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (contact) => {
        setContactToDelete(contact);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!contactToDelete) return;

        setDeleting(true);
        try {
            await axios.delete(`${API}/contacts/${contactToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Contacto eliminado');
            await fetchContacts();
        } catch (error) {
            console.error('Error deleting contact:', error);
            toast.error('Error al eliminar contacto');
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setContactToDelete(null);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                    Contactos ({contacts.length})
                </h3>
                <Button onClick={handleOpenCreateModal} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Contacto
                </Button>
            </div>

            {/* Contacts List */}
            {contacts.length === 0 ? (
                <div className="p-12 text-center">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No hay contactos registrados</p>
                    <p className="text-sm text-gray-400">Agrega contactos para este cliente</p>
                </div>
            ) : (
                <div className="divide-y divide-gray-200">
                    {contacts.map(contact => (
                        <div key={contact.id} className="px-6 py-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    {/* Name and Role */}
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                                            {contact.name ? contact.name.charAt(0).toUpperCase() : 'C'}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {contact.name}
                                            </div>
                                            {contact.role && (
                                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                                    <Briefcase className="h-3 w-3" />
                                                    {contact.role}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Contact Info */}
                                    <div className="ml-13 space-y-1">
                                        {contact.email && (
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <Mail className="h-4 w-4 text-gray-400" />
                                                <a href={`mailto:${contact.email}`} className="hover:text-blue-600">
                                                    {contact.email}
                                                </a>
                                            </div>
                                        )}
                                        {contact.phone && (
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <Phone className="h-4 w-4 text-gray-400" />
                                                <a href={`tel:${contact.phone}`} className="hover:text-blue-600">
                                                    {contact.phone}
                                                </a>
                                            </div>
                                        )}
                                        {contact.notes && (
                                            <div className="flex items-start gap-2 text-sm text-gray-600 mt-2">
                                                <StickyNote className="h-4 w-4 text-gray-400 mt-0.5" />
                                                <span className="italic">{contact.notes}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1 ml-4">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleOpenEditModal(contact)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteClick(contact)}
                                        className="text-gray-400 hover:text-red-600"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {editingContact ? 'Editar Contacto' : 'Nuevo Contacto'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingContact
                                ? 'Modifica los datos del contacto.'
                                : 'Agrega un nuevo contacto para este cliente.'
                            }
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Name */}
                        <div>
                            <Label htmlFor="name">Nombre *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Juan Pérez"
                                disabled={saving}
                                className="mt-1"
                            />
                        </div>

                        {/* Role */}
                        <div>
                            <Label htmlFor="role">Rol / Descripción</Label>
                            <Input
                                id="role"
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                placeholder="Ej: Pagos, Notario, Asesor externo"
                                disabled={saving}
                                className="mt-1"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="contacto@ejemplo.com"
                                disabled={saving}
                                className="mt-1"
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <Label htmlFor="phone">Teléfono</Label>
                            <Input
                                id="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="+52 55 1234 5678"
                                disabled={saving}
                                className="mt-1"
                            />
                        </div>

                        {/* Notes */}
                        <div>
                            <Label htmlFor="notes">Notas</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Notas adicionales..."
                                disabled={saving}
                                className="mt-1"
                                rows={3}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleCloseModal}
                            disabled={saving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving || !formData.name.trim()}
                            className="bg-gray-900 hover:bg-gray-800"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                editingContact ? 'Guardar' : 'Crear'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar contacto?</AlertDialogTitle>
                        <AlertDialogDescription>
                            El contacto <span className="font-semibold">{contactToDelete?.name}</span> será eliminado permanentemente.
                            Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Eliminando...
                                </>
                            ) : (
                                'Eliminar'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
